import { useState, useEffect } from "react";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { Badge } from "@/components/ui/badge";
import { Separator } from "@/components/ui/separator";
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogTrigger,
} from "@/components/ui/dialog";
import { scooterApi, formatPrice } from "@/lib/api";
import { toast } from "sonner";
import { 
  DollarSign, 
  TrendingUp, 
  User, 
  Phone, 
  Mail,
  CreditCard,
  Calendar,
  Package,
  CheckCircle,
  Edit
} from "lucide-react";

const PAYMENT_METHODS = {
  cash: { label: "Cash / Numerar", icon: "💵" },
  transfer: { label: "Virement / Transfer", icon: "🏦" },
  card: { label: "Carte / Card", icon: "💳" },
  financing: { label: "Financement / Finanțare", icon: "📋" }
};

export function SaleRecord({ scooter, onUpdate }) {
  const [isDialogOpen, setIsDialogOpen] = useState(false);
  const [loading, setLoading] = useState(false);
  const [saleData, setSaleData] = useState({
    sale_date: new Date().toISOString().split('T')[0],
    sale_price: scooter?.selling_price || 0,
    buyer_name: "",
    buyer_phone: "",
    buyer_email: "",
    payment_method: "cash",
    notes: ""
  });

  const existingRecord = scooter?.sale_record;

  useEffect(() => {
    if (existingRecord) {
      setSaleData(existingRecord);
    }
  }, [existingRecord]);

  const handleSaveSale = async () => {
    if (!saleData.sale_date || !saleData.sale_price) {
      toast.error("Date et prix de vente obligatoires / Data și prețul vânzării sunt obligatorii");
      return;
    }

    setLoading(true);
    try {
      await scooterApi.update(scooter.scooter_id, {
        sale_record: saleData,
        status: "sold"  // Automatically mark as sold
      });
      toast.success("Vente enregistrée / Vânzare înregistrată");
      setIsDialogOpen(false);
      onUpdate?.();
    } catch (error) {
      toast.error("Erreur lors de l'enregistrement / Eroare la înregistrare");
    } finally {
      setLoading(false);
    }
  };

  const formatDate = (dateStr) => {
    if (!dateStr) return "-";
    return new Date(dateStr).toLocaleDateString("ro-RO", {
      day: "2-digit",
      month: "long",
      year: "numeric"
    });
  };

  // Calculate profit
  const purchasePrice = scooter?.purchase_price || 0;
  const salePrice = existingRecord?.sale_price || scooter?.selling_price || 0;
  const profit = salePrice - purchasePrice;
  const profitPercentage = purchasePrice > 0 ? ((profit / purchasePrice) * 100).toFixed(1) : 0;

  return (
    <Card className="border-border" data-testid="sale-record">
      <CardHeader className="pb-4">
        <div className="flex items-center justify-between">
          <CardTitle className="flex items-center gap-2 text-lg">
            <DollarSign className="w-5 h-5 text-green-500" />
            Vente / Vânzare
            {scooter?.status === "sold" && (
              <Badge className="bg-red-500 text-white">VENDU / VÂNDUT</Badge>
            )}
          </CardTitle>
          <Dialog open={isDialogOpen} onOpenChange={setIsDialogOpen}>
            <DialogTrigger asChild>
              <Button size="sm" variant={existingRecord ? "outline" : "default"} data-testid="btn-record-sale">
                {existingRecord ? (
                  <>
                    <Edit className="w-4 h-4 mr-2" />
                    Modifier
                  </>
                ) : (
                  <>
                    <DollarSign className="w-4 h-4 mr-2" />
                    Enregistrer vente
                  </>
                )}
              </Button>
            </DialogTrigger>
            <DialogContent className="max-w-md">
              <DialogHeader>
                <DialogTitle className="flex items-center gap-2">
                  <DollarSign className="w-5 h-5 text-green-500" />
                  {existingRecord ? "Modifier la vente" : "Enregistrer une vente"}
                </DialogTitle>
              </DialogHeader>
              <div className="space-y-4 mt-4">
                <div className="grid grid-cols-2 gap-4">
                  <div className="space-y-2">
                    <Label htmlFor="sale-date">Date de vente *</Label>
                    <Input
                      id="sale-date"
                      type="date"
                      value={saleData.sale_date}
                      onChange={(e) => setSaleData({...saleData, sale_date: e.target.value})}
                      data-testid="input-sale-date"
                    />
                  </div>
                  <div className="space-y-2">
                    <Label htmlFor="sale-price">Prix de vente (EUR) *</Label>
                    <Input
                      id="sale-price"
                      type="number"
                      value={saleData.sale_price}
                      onChange={(e) => setSaleData({...saleData, sale_price: parseFloat(e.target.value) || 0})}
                      data-testid="input-sale-price"
                    />
                  </div>
                </div>

                <Separator />

                <div className="space-y-2">
                  <Label htmlFor="buyer-name">Nom de l'acheteur / Numele cumpărătorului</Label>
                  <Input
                    id="buyer-name"
                    placeholder="Nom complet..."
                    value={saleData.buyer_name}
                    onChange={(e) => setSaleData({...saleData, buyer_name: e.target.value})}
                    data-testid="input-buyer-name"
                  />
                </div>

                <div className="grid grid-cols-2 gap-4">
                  <div className="space-y-2">
                    <Label htmlFor="buyer-phone">Téléphone</Label>
                    <Input
                      id="buyer-phone"
                      type="tel"
                      placeholder="07XX XXX XXX"
                      value={saleData.buyer_phone}
                      onChange={(e) => setSaleData({...saleData, buyer_phone: e.target.value})}
                      data-testid="input-buyer-phone"
                    />
                  </div>
                  <div className="space-y-2">
                    <Label htmlFor="buyer-email">Email</Label>
                    <Input
                      id="buyer-email"
                      type="email"
                      placeholder="email@..."
                      value={saleData.buyer_email}
                      onChange={(e) => setSaleData({...saleData, buyer_email: e.target.value})}
                      data-testid="input-buyer-email"
                    />
                  </div>
                </div>

                <div className="space-y-2">
                  <Label htmlFor="payment-method">Mode de paiement</Label>
                  <select
                    id="payment-method"
                    value={saleData.payment_method}
                    onChange={(e) => setSaleData({...saleData, payment_method: e.target.value})}
                    className="flex h-10 w-full rounded-md border border-input bg-background px-3 py-2 text-sm"
                    data-testid="select-payment-method"
                  >
                    {Object.entries(PAYMENT_METHODS).map(([key, { label, icon }]) => (
                      <option key={key} value={key}>{icon} {label}</option>
                    ))}
                  </select>
                </div>

                <div className="space-y-2">
                  <Label htmlFor="sale-notes">Notes</Label>
                  <Textarea
                    id="sale-notes"
                    placeholder="Détails ou observations..."
                    value={saleData.notes}
                    onChange={(e) => setSaleData({...saleData, notes: e.target.value})}
                    rows={2}
                    data-testid="input-sale-notes"
                  />
                </div>

                {/* Profit Preview */}
                <div className="p-3 bg-secondary rounded-lg">
                  <div className="flex justify-between text-sm">
                    <span className="text-muted-foreground">Prix d'achat:</span>
                    <span className="font-mono">{formatPrice(purchasePrice)}</span>
                  </div>
                  <div className="flex justify-between text-sm">
                    <span className="text-muted-foreground">Prix de vente:</span>
                    <span className="font-mono">{formatPrice(saleData.sale_price)}</span>
                  </div>
                  <Separator className="my-2" />
                  <div className="flex justify-between font-bold">
                    <span>Profit:</span>
                    <span className={`font-mono ${saleData.sale_price - purchasePrice >= 0 ? 'text-green-500' : 'text-red-500'}`}>
                      {formatPrice(saleData.sale_price - purchasePrice)}
                    </span>
                  </div>
                </div>

                <Button 
                  className="w-full" 
                  onClick={handleSaveSale}
                  disabled={loading}
                  data-testid="btn-save-sale"
                >
                  {loading ? "Se salvează..." : existingRecord ? "Modifier / Modifică" : "Enregistrer / Înregistrează"}
                </Button>
              </div>
            </DialogContent>
          </Dialog>
        </div>
      </CardHeader>
      <CardContent>
        {existingRecord ? (
          <div className="space-y-4">
            {/* Sale Summary */}
            <div className="grid grid-cols-2 gap-4">
              <div className="p-3 bg-green-500/10 rounded-lg text-center">
                <p className="text-xs text-muted-foreground mb-1">Prix de vente</p>
                <p className="font-mono font-bold text-xl text-green-500">
                  {formatPrice(existingRecord.sale_price)}
                </p>
              </div>
              <div className={`p-3 rounded-lg text-center ${profit >= 0 ? 'bg-green-500/10' : 'bg-red-500/10'}`}>
                <p className="text-xs text-muted-foreground mb-1">Profit</p>
                <p className={`font-mono font-bold text-xl ${profit >= 0 ? 'text-green-500' : 'text-red-500'}`}>
                  {formatPrice(profit)}
                </p>
                <p className="text-xs text-muted-foreground">
                  ({profitPercentage}%)
                </p>
              </div>
            </div>

            <Separator />

            {/* Sale Details */}
            <div className="space-y-3">
              <div className="flex items-center gap-3 text-sm">
                <Calendar className="w-4 h-4 text-muted-foreground" />
                <span className="text-muted-foreground">Date:</span>
                <span className="font-medium">{formatDate(existingRecord.sale_date)}</span>
              </div>

              {existingRecord.buyer_name && (
                <div className="flex items-center gap-3 text-sm">
                  <User className="w-4 h-4 text-muted-foreground" />
                  <span className="text-muted-foreground">Acheteur:</span>
                  <span className="font-medium">{existingRecord.buyer_name}</span>
                </div>
              )}

              {existingRecord.buyer_phone && (
                <div className="flex items-center gap-3 text-sm">
                  <Phone className="w-4 h-4 text-muted-foreground" />
                  <span className="text-muted-foreground">Téléphone:</span>
                  <a href={`tel:${existingRecord.buyer_phone}`} className="font-mono text-primary hover:underline">
                    {existingRecord.buyer_phone}
                  </a>
                </div>
              )}

              {existingRecord.buyer_email && (
                <div className="flex items-center gap-3 text-sm">
                  <Mail className="w-4 h-4 text-muted-foreground" />
                  <span className="text-muted-foreground">Email:</span>
                  <a href={`mailto:${existingRecord.buyer_email}`} className="text-primary hover:underline">
                    {existingRecord.buyer_email}
                  </a>
                </div>
              )}

              <div className="flex items-center gap-3 text-sm">
                <CreditCard className="w-4 h-4 text-muted-foreground" />
                <span className="text-muted-foreground">Paiement:</span>
                <Badge variant="outline">
                  {PAYMENT_METHODS[existingRecord.payment_method]?.icon} {PAYMENT_METHODS[existingRecord.payment_method]?.label || existingRecord.payment_method}
                </Badge>
              </div>

              {existingRecord.notes && (
                <div className="p-2 bg-secondary/50 rounded text-sm italic text-muted-foreground">
                  {existingRecord.notes}
                </div>
              )}
            </div>
          </div>
        ) : (
          <div className="text-center py-6 text-muted-foreground">
            <Package className="w-12 h-12 mx-auto mb-3 opacity-30" />
            <p>Ce scooter n'est pas encore vendu</p>
            <p className="text-sm">Acest scuter nu este încă vândut</p>
            <div className="mt-4 p-3 bg-secondary/50 rounded-lg">
              <p className="text-xs text-muted-foreground mb-1">Prix suggéré / Preț sugerat:</p>
              <p className="font-mono font-bold text-xl text-primary">
                {formatPrice(scooter?.selling_price || 0)}
              </p>
            </div>
          </div>
        )}
      </CardContent>
    </Card>
  );
}

export default SaleRecord;
